import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  FileText, Plus, Pencil, Trash2, Upload, X, Send, EyeOff, Eye,
  Loader2, Image as ImageIcon, AlertTriangle, Calendar, Package, MapPin,
  ExternalLink, Mail,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Pre-defined issue checkboxes the admin can tick on a report.
// Free-text "Other notes" still available below the list.
const ISSUE_OPTIONS = [
  'Some addresses not delivered',
  'Wrong address provided by client',
  'Weather-related delays',
  'Access issues (gated / locked entry)',
  'Worker no-show / partial completion',
  'Apartment / multi-unit complications',
  'Do-not-deliver list updated',
  'Other (see notes)',
];

const STATUS_BADGE = {
  draft: { label: 'Draft', cls: 'bg-amber-100 text-amber-700 border-amber-300' },
  published: { label: 'Published', cls: 'bg-emerald-100 text-emerald-700 border-emerald-300' },
};

// Short, friendly relative-time string for "Last sent" hints. Kept tiny on
// purpose — the absolute timestamp is in the tooltip so the admin can hover
// to see the exact moment if "2h ago" feels too vague.
const _relTime = (iso) => {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const diff = (Date.now() - t) / 1000;
  if (diff < 45) return 'just now';
  if (diff < 90) return '1m ago';
  if (diff < 60 * 60) return `${Math.round(diff / 60)}m ago`;
  if (diff < 60 * 60 * 36) return `${Math.round(diff / 3600)}h ago`;
  if (diff < 60 * 60 * 24 * 14) return `${Math.round(diff / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
};

const AdminClientReporting = () => {
  const { getAuthHeader, userType, adminRole } = useAuth();
  const role = adminRole || userType || 'admin';
  const canDelete = role === 'admin';

  const [reports, setReports] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingReport, setEditingReport] = useState(null); // null = new, object = edit
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileRef = useRef(null);

  // ---- Empty form scaffold ----
  // Default report_type='daily_update' — the legacy 'completion' / 'daily'
  // types are still accepted by the backend for back-compat with any
  // older drafts, but the simplified "Daily Update and Photos Report"
  // form is now the only creation path. All legacy fields stay in the
  // shape so editing an old report doesn't crash; the unused ones are
  // simply ignored on submit.
  const emptyForm = {
    job_id: '',
    report_type: 'daily_update',
    title: '',
    client_email: '',  // explicit override; empty = use job's client_email
    delivery_date_start: '',
    delivery_date_end: '',
    activity_date: '',             // daily-report only
    deliveries_count: '',
    summary: '',
    issues_text: '',
    issues_checked: [],
    include_route_map_snapshot: false,
    include_worker_photos: false,
    selected_worker_photo_ids: [],
  };
  const [form, setForm] = useState(emptyForm);

  // Auto-calculated daily delivery count for the picked job + activity_date.
  // Reads from the backend so it stays in sync with worker mark data.
  const [dailyAutoCount, setDailyAutoCount] = useState(null);
  const [dailyAutoLoading, setDailyAutoLoading] = useState(false);

  // Preview state — admin-only differentiation of delivery dots. Populated
  // whenever a job is picked, and refreshed when the form's job_id changes.
  // Clients NEVER see this breakdown — their view of any published report
  // stays a single unified set of dots.
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showAdminLayer, setShowAdminLayer] = useState(true);
  const [showWorkerLayer, setShowWorkerLayer] = useState(true);

  // Worker delivery photo picker — populated when the admin checks
  // "Include worker delivery photos". The admin then ticks individual photos
  // to include in the client report.
  const [workerPhotos, setWorkerPhotos] = useState([]);
  const [workerPhotosLoading, setWorkerPhotosLoading] = useState(false);
  const [sendingPreview, setSendingPreview] = useState(false);

  // Cached blob URL for the email-style map snapshot. We re-fetch when the
  // job changes; refresh button re-renders for the same job (useful after
  // workers log more deliveries while the admin has the form open).
  const [emailMapUrl, setEmailMapUrl] = useState(null);
  const [emailMapLoading, setEmailMapLoading] = useState(false);
  const [emailMapError, setEmailMapError] = useState(null);
  const emailMapBlobRef = useRef(null);

  const fetchPreview = async (jobId) => {
    if (!jobId) { setPreview(null); return; }
    setPreviewLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/manual-reports/job-preview/${jobId}`,
        getAuthHeader(),
      );
      setPreview(data);
    } catch {
      setPreview(null);
    } finally {
      setPreviewLoading(false);
    }
  };

  // Pull every worker delivery photo for the report's job (parent + children).
  // Only called when the admin has saved a draft and toggled on
  // "Include worker delivery photos" — keeps the network footprint small.
  const fetchWorkerPhotos = async (reportId) => {
    if (!reportId) {
      setWorkerPhotos([]);
      return;
    }
    setWorkerPhotosLoading(true);
    try {
      const { data } = await axios.get(
        `${API}/admin/manual-reports/${reportId}/worker-photos`,
        getAuthHeader(),
      );
      setWorkerPhotos(data?.photos || []);
    } catch (e) {
      setWorkerPhotos([]);
    } finally {
      setWorkerPhotosLoading(false);
    }
  };

  const toggleWorkerPhotoSelection = (photoId) => {
    setForm((prev) => {
      const ids = new Set(prev.selected_worker_photo_ids || []);
      if (ids.has(photoId)) ids.delete(photoId); else ids.add(photoId);
      return { ...prev, selected_worker_photo_ids: Array.from(ids) };
    });
  };

  const selectAllWorkerPhotos = () => {
    setForm((prev) => ({
      ...prev,
      selected_worker_photo_ids: workerPhotos.map((p) => p.id),
    }));
  };

  const clearWorkerPhotoSelection = () => {
    setForm((prev) => ({ ...prev, selected_worker_photo_ids: [] }));
  };

  // Auto-load worker photos whenever the checkbox flips on and we have a draft
  useEffect(() => {
    if (form.include_worker_photos && editingReport?.id) {
      fetchWorkerPhotos(editingReport.id);
    } else {
      setWorkerPhotos([]);
    }
     
  }, [form.include_worker_photos, editingReport?.id]);

  // ---- Email-style map snapshot ----
  // Fetches the SAME PNG the preview email will embed, so the admin can
  // eyeball it before sending. Stored as a blob URL because the endpoint
  // is auth-gated (can't use <img src> directly with the bearer token).
  const fetchEmailMapSnapshot = async (jobId) => {
    if (!jobId) {
      setEmailMapUrl(null);
      setEmailMapError(null);
      return;
    }
    // Free any previous blob URL so memory doesn't leak across job switches.
    if (emailMapBlobRef.current) {
      URL.revokeObjectURL(emailMapBlobRef.current);
      emailMapBlobRef.current = null;
    }
    setEmailMapLoading(true);
    setEmailMapError(null);
    try {
      const res = await axios.get(
        `${API}/admin/manual-reports/job-preview/${jobId}/map-snapshot.png`,
        { ...getAuthHeader(), responseType: 'blob' },
      );
      const url = URL.createObjectURL(res.data);
      emailMapBlobRef.current = url;
      setEmailMapUrl(url);
    } catch (e) {
      setEmailMapUrl(null);
      setEmailMapError(
        e?.response?.status === 404
          ? 'No delivery activity yet — the email will go out without a map.'
          : 'Could not render the map preview.',
      );
    } finally {
      setEmailMapLoading(false);
    }
  };

  // Refetch the snapshot whenever the picked job changes.
  useEffect(() => {
    fetchEmailMapSnapshot(form.job_id);
    // Cleanup on unmount: revoke the blob URL.
    return () => {
      if (emailMapBlobRef.current) {
        URL.revokeObjectURL(emailMapBlobRef.current);
        emailMapBlobRef.current = null;
      }
    };
     
  }, [form.job_id]);

  useEffect(() => {
    loadAll();
  }, []);

  // Deep-link auto-open: ?openNewReport=1&jobId=<uuid> opens the New
  // "Daily Update and Photos Report" dialog with the job pre-selected.
  // Used by the "+ Daily Update" shortcut button on the Admin Job Detail
  // modal so admins can post an update without leaving the job context.
  // Search params are cleared after consuming so a refresh doesn't
  // re-open the dialog unexpectedly.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (!jobs.length) return; // wait until jobs list is loaded
    if (searchParams.get('openNewReport') !== '1') return;
    const targetJobId = searchParams.get('jobId') || '';
    const targetJob = jobs.find((j) => j.id === targetJobId);
    if (!targetJob) {
      toast.error('That job is not eligible for a daily update report yet.');
      // Still clean up the URL so the warning doesn't loop on every render.
      const next = new URLSearchParams(searchParams);
      next.delete('openNewReport');
      next.delete('jobId');
      setSearchParams(next, { replace: true });
      return;
    }
    setEditingReport(null);
    setForm({ ...emptyForm, job_id: targetJobId });
    setShowForm(true);
    // onJobPicked also prefills the preview + client email defaults
    onJobPicked(targetJobId);
    const next = new URLSearchParams(searchParams);
    next.delete('openNewReport');
    next.delete('jobId');
    setSearchParams(next, { replace: true });
     
  }, [jobs, searchParams]);

  // Daily report: refetch the auto-calculated deliveries count whenever
  // the picked job or activity_date changes. The form pre-fills the
  // count field from this value so the admin sees the worker-data
  // truth before publishing.
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      if (form.report_type !== 'daily' || !form.job_id || !form.activity_date) {
        setDailyAutoCount(null);
        return;
      }
      setDailyAutoLoading(true);
      try {
        const { data } = await axios.get(
          `${API}/admin/manual-reports/job-preview/${form.job_id}/daily-stats`,
          { ...getAuthHeader(), params: { activity_date: form.activity_date } },
        );
        if (cancelled) return;
        const v = Number(data?.deliveries_count || 0);
        setDailyAutoCount(v);
        // Only pre-fill the field if the admin hasn't entered an override.
        setForm((prev) => {
          if (prev.deliveries_count !== '' && prev.deliveries_count != null) return prev;
          return { ...prev, deliveries_count: String(v) };
        });
      } catch (_) {
        if (!cancelled) setDailyAutoCount(null);
      } finally {
        if (!cancelled) setDailyAutoLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
     
  }, [form.report_type, form.job_id, form.activity_date]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [reportsRes, jobsRes] = await Promise.all([
        axios.get(`${API}/admin/manual-reports`, getAuthHeader()),
        axios.get(`${API}/admin/manual-reports/jobs-for-report`, getAuthHeader()),
      ]);
      setReports(reportsRes.data || []);
      setJobs(jobsRes.data || []);
    } catch (e) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  // ---- Form helpers ----
  const openNew = () => {
    setEditingReport(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const openEdit = (r) => {
    setEditingReport(r);
    setForm({
      job_id: r.job_id || '',
      report_type: r.report_type || 'completion',
      title: r.title || '',
      client_email: r.client_email || '',
      delivery_date_start: r.delivery_date_start || '',
      delivery_date_end: r.delivery_date_end || '',
      activity_date: r.activity_date || '',
      deliveries_count: r.deliveries_count == null ? '' : String(r.deliveries_count),
      summary: r.summary || '',
      issues_text: r.issues_text || '',
      issues_checked: r.issues_checked || [],
      include_route_map_snapshot: !!r.include_route_map_snapshot,
      include_worker_photos: !!r.include_worker_photos,
      selected_worker_photo_ids: r.selected_worker_photo_ids || [],
    });
    setShowForm(true);
    fetchPreview(r.job_id);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingReport(null);
    setForm(emptyForm);
    setPreview(null);
    setShowAdminLayer(true);
    setShowWorkerLayer(true);
  };

  // When job_id changes for a NEW report, prefill defaults from the picked job.
  const onJobPicked = (id) => {
    const j = jobs.find((x) => x.id === id);
    setForm((prev) => ({
      ...prev,
      job_id: id,
      title: prev.title || (j ? `Delivery Report — ${j.title}` : ''),
      deliveries_count:
        prev.deliveries_count !== ''
          ? prev.deliveries_count
          : j?.deliveries_completed != null
          ? String(j.deliveries_completed)
          : j?.deliveries != null
          ? String(j.deliveries)
          : '',
    }));
    // Pull the preview right away so the auditor sees the delivery-dot
    // breakdown (admin vs worker) for the picked job.
    fetchPreview(id);
  };

  const toggleIssue = (issue) => {
    setForm((prev) => ({
      ...prev,
      issues_checked: prev.issues_checked.includes(issue)
        ? prev.issues_checked.filter((i) => i !== issue)
        : [...prev.issues_checked, issue],
    }));
  };

  // ---- Save (create or edit), with optional immediate publish flag ----
  const save = async (publishImmediately = false) => {
    if (!form.job_id) {
      toast.error('Please pick a job');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        deliveries_count: form.deliveries_count === '' ? null : Number(form.deliveries_count),
      };
      let saved;
      if (editingReport) {
        const { data } = await axios.put(
          `${API}/admin/manual-reports/${editingReport.id}`,
          payload,
          getAuthHeader(),
        );
        saved = data;
        if (publishImmediately && saved.status !== 'published') {
          const r = await axios.post(
            `${API}/admin/manual-reports/${saved.id}/publish`,
            {},
            getAuthHeader(),
          );
          saved = r.data;
        }
      } else {
        const createBody = { ...payload, status: publishImmediately ? 'published' : 'draft' };
        const { data } = await axios.post(
          `${API}/admin/manual-reports`,
          createBody,
          getAuthHeader(),
        );
        saved = data;
      }
      toast.success(
        publishImmediately ? 'Report published to client portal' : 'Draft saved',
      );
      await loadAll();
      // Keep the form open in EDIT mode on the new record so the user can
      // attach photos right away if it was just created.
      setEditingReport(saved);
      setForm({
        job_id: saved.job_id || '',
        report_type: saved.report_type || 'completion',
        title: saved.title || '',
        client_email: saved.client_email || '',
        delivery_date_start: saved.delivery_date_start || '',
        delivery_date_end: saved.delivery_date_end || '',
        activity_date: saved.activity_date || '',
        deliveries_count: saved.deliveries_count == null ? '' : String(saved.deliveries_count),
        summary: saved.summary || '',
        issues_text: saved.issues_text || '',
        issues_checked: saved.issues_checked || [],
        include_route_map_snapshot: !!saved.include_route_map_snapshot,
        include_worker_photos: !!saved.include_worker_photos,
        selected_worker_photo_ids: saved.selected_worker_photo_ids || [],
      });
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const togglePublish = async (r) => {
    try {
      const ep = r.status === 'published' ? 'unpublish' : 'publish';
      await axios.post(`${API}/admin/manual-reports/${r.id}/${ep}`, {}, getAuthHeader());
      toast.success(r.status === 'published' ? 'Unpublished' : 'Published');
      await loadAll();
    } catch (e) {
      toast.error('Action failed');
    }
  };

  // Per-row spinners so the table can show which report is being acted on
  // without disabling every button on the page.
  const [resendingId, setResendingId] = useState(null);
  const [linkLoadingId, setLinkLoadingId] = useState(null);

  // Re-send the published report to the client's saved email. Backend
  // enforces "published only" — if the admin clicks this on a draft we
  // bubble that error up verbatim instead of silently failing.
  const resendToClient = async (r) => {
    if (!window.confirm(
      `Re-send "${r.title}" to ${r.client_email || 'the client'}?`,
    )) return;
    setResendingId(r.id);
    try {
      const { data } = await axios.post(
        `${API}/admin/manual-reports/${r.id}/resend-to-client`, {}, getAuthHeader(),
      );
      toast.success(`Email sent to ${data?.recipient || r.client_email}`);
      // Refresh so the new last_sent_to_client_at shows in the row hint.
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Re-send failed');
    } finally {
      setResendingId(null);
    }
  };

  // Open the branded client portal in a new tab. Admin must already be
  // signed in as that client (or copy the URL to share). We fetch the
  // backend-resolved URL so the link always uses the configured branded
  // host (lfdportal.com), never the platform preview origin.
  const openClientPortal = async (r) => {
    setLinkLoadingId(r.id);
    try {
      const { data } = await axios.get(
        `${API}/admin/manual-reports/${r.id}/portal-link`, getAuthHeader(),
      );
      const url = data?.reports_url || data?.portal_url;
      if (!url) {
        toast.error('Portal URL not configured on the backend.');
        return;
      }
      // Copy to clipboard as a convenience so the admin can paste it into
      // a chat with the client if they prefer that over opening it here.
      try { await navigator.clipboard?.writeText(url); } catch (_) { /* ignore */ }
      window.open(url, '_blank', 'noopener,noreferrer');
      toast.success(
        `Opened client portal · link copied (${data?.client_email || 'client'} must sign in)`,
      );
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load portal link');
    } finally {
      setLinkLoadingId(null);
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete report "${r.title}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/manual-reports/${r.id}`, getAuthHeader());
      toast.success('Report deleted');
      await loadAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  // ---- Photo upload — auto-creates a draft if user hasn't saved yet so the
  // Upload button is usable from the very first time the form is opened.
  // Accepts either a single File or an array/FileList for bulk uploads.
  const MAX_PHOTOS_PER_BATCH = 10;
  const uploadPhotos = async (files) => {
    // Normalise into a plain array for `files.length` + slicing
    const list = Array.from(files || []);
    if (list.length === 0) return;
    // Cap the batch at 10 photos so the user can't accidentally drag 200
    // raw camera-roll shots in one go and stall the form.
    let trimmedNotice = '';
    let toUpload = list;
    if (list.length > MAX_PHOTOS_PER_BATCH) {
      toUpload = list.slice(0, MAX_PHOTOS_PER_BATCH);
      trimmedNotice = ` (uploaded the first ${MAX_PHOTOS_PER_BATCH}; pick the next batch and click Upload again)`;
    }

    let report = editingReport;
    if (!report) {
      if (!form.job_id) {
        toast.error('Pick a job first');
        return;
      }
      // Auto-save draft inline so we have a report id to attach photos to
      try {
        setSaving(true);
        const createBody = {
          ...form,
          deliveries_count: form.deliveries_count === '' ? null : Number(form.deliveries_count),
          status: 'draft',
        };
        const { data } = await axios.post(
          `${API}/admin/manual-reports`,
          createBody,
          getAuthHeader(),
        );
        report = data;
        setEditingReport(data);
        await loadAll();
      } catch (e) {
        toast.error('Could not auto-save draft — try Save Draft first');
        setSaving(false);
        return;
      } finally {
        setSaving(false);
      }
    }
    setUploadingPhoto(true);
    let successCount = 0;
    let failCount = 0;
    try {
      // Sequential uploads — keeps backend disk + db writes ordered and
      // means we don't have to worry about thumb-generation contention.
      for (const file of toUpload) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(
            `${API}/admin/manual-reports/${report.id}/photos`,
            fd,
            { ...getAuthHeader(), headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' } },
          );
          successCount++;
        } catch (err) {
          failCount++;
          console.warn(`Photo upload failed for ${file.name}:`, err?.message);
        }
      }
      // Refresh the report so all newly-uploaded photos appear in one batch
      try {
        const { data } = await axios.get(
          `${API}/admin/manual-reports/${report.id}`,
          getAuthHeader(),
        );
        setEditingReport(data);
      } catch {
        // non-fatal: thumbnails may lag a beat
      }
      if (successCount > 0 && failCount === 0) {
        toast.success(
          `Uploaded ${successCount} photo${successCount === 1 ? '' : 's'}${trimmedNotice}`,
        );
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`Uploaded ${successCount}, ${failCount} failed${trimmedNotice}`);
      } else {
        toast.error('All uploads failed');
      }
    } finally {
      setUploadingPhoto(false);
    }
  };
  // Back-compat alias for any single-file callers
  const uploadPhoto = (file) => uploadPhotos([file]);

  // -------------------------------------------------------------------
  // Custom map snapshots — admin uploads screenshots to render instead of
  // (or in addition to) the auto-generated route map snapshot. Multiple
  // allowed. Stored in `custom_map_snapshots` on the report doc, separate
  // from the regular delivery `photos` so they don't get rotated 90° by
  // the worker-photo viewer logic and stay grouped together visually.
  // -------------------------------------------------------------------
  const [uploadingMapSnap, setUploadingMapSnap] = useState(false);

  const uploadMapSnapshots = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;

    let report = editingReport;
    if (!report) {
      if (!form.job_id) {
        toast.error('Pick a job first');
        return;
      }
      try {
        const createBody = {
          ...form,
          deliveries_count: form.deliveries_count === '' ? null : Number(form.deliveries_count),
          status: 'draft',
        };
        const { data } = await axios.post(`${API}/admin/manual-reports`, createBody, getAuthHeader());
        report = data;
        setEditingReport(data);
        await loadAll();
      } catch (_) {
        toast.error('Could not auto-save draft — try Save Draft first');
        return;
      }
    }
    setUploadingMapSnap(true);
    let ok = 0;
    let fail = 0;
    try {
      for (const file of list) {
        try {
          const fd = new FormData();
          fd.append('file', file);
          await axios.post(
            `${API}/admin/manual-reports/${report.id}/map-snapshots`,
            fd,
            { ...getAuthHeader(), headers: { ...getAuthHeader().headers, 'Content-Type': 'multipart/form-data' } },
          );
          ok++;
        } catch (err) {
          fail++;
          console.warn(`Map snapshot upload failed for ${file.name}:`, err?.message);
        }
      }
      try {
        const { data } = await axios.get(`${API}/admin/manual-reports/${report.id}`, getAuthHeader());
        setEditingReport(data);
      } catch { /* non-fatal */ }
      if (ok > 0 && fail === 0) toast.success(`Uploaded ${ok} map snapshot${ok === 1 ? '' : 's'}`);
      else if (ok > 0) toast.warning(`Uploaded ${ok}, ${fail} failed`);
      else toast.error('All map snapshot uploads failed');
    } finally {
      setUploadingMapSnap(false);
    }
  };

  const deleteMapSnapshot = async (snapId) => {
    if (!editingReport) return;
    try {
      await axios.delete(
        `${API}/admin/manual-reports/${editingReport.id}/map-snapshots/${snapId}`,
        getAuthHeader(),
      );
      const { data } = await axios.get(
        `${API}/admin/manual-reports/${editingReport.id}`,
        getAuthHeader(),
      );
      setEditingReport(data);
    } catch (e) {
      toast.error('Could not remove map snapshot');
    }
  };

  // ---- Send Preview — emails the current report to the configured preview
  // recipient (info@localflyerdelivery.com). Will auto-save the draft first
  // so users don't have to remember the two-step click sequence.
  const sendPreview = async () => {
    if (!form.job_id) {
      toast.error('Pick a job first');
      return;
    }
    setSendingPreview(true);
    try {
      // Save (or update) the report so the latest form state is what the
      // preview email reflects.
      let reportId = editingReport?.id;
      const payload = {
        ...form,
        deliveries_count: form.deliveries_count === '' ? null : Number(form.deliveries_count),
      };
      if (editingReport) {
        await axios.put(
          `${API}/admin/manual-reports/${editingReport.id}`,
          payload,
          getAuthHeader(),
        );
      } else {
        const { data } = await axios.post(
          `${API}/admin/manual-reports`,
          { ...payload, status: 'draft' },
          getAuthHeader(),
        );
        reportId = data.id;
        setEditingReport(data);
        await loadAll();
      }
      const { data } = await axios.post(
        `${API}/admin/manual-reports/${reportId}/send-preview`,
        {},
        getAuthHeader(),
      );
      toast.success(`Preview emailed to ${data?.recipient || 'preview recipient'}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send preview');
    } finally {
      setSendingPreview(false);
    }
  };

  const deletePhoto = async (photoId) => {
    if (!editingReport) return;
    try {
      await axios.delete(
        `${API}/admin/manual-reports/${editingReport.id}/photos/${photoId}`,
        getAuthHeader(),
      );
      const { data } = await axios.get(
        `${API}/admin/manual-reports/${editingReport.id}`,
        getAuthHeader(),
      );
      setEditingReport(data);
    } catch (e) {
      toast.error('Could not remove photo');
    }
  };

  // =========================================================================
  // RENDER
  // =========================================================================
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-purple-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="text-purple-600" size={26} /> Client Reports
          </h1>
          <p className="text-sm text-gray-500 mt-1 max-w-3xl">
            Manually authored delivery reports. Once published, the report appears
            in the client's portal. Auto-generated reports remain visible to clients
            that don't yet have a manual report (legacy fallback).
          </p>
        </div>
        <Button
          onClick={openNew}
          className="bg-purple-600 hover:bg-purple-700 text-white"
          data-testid="new-report-btn"
        >
          <Plus size={16} className="mr-1" /> New Report
        </Button>
      </div>

      {/* Why-this-is-manual notice */}
      <Card className="p-4 border-amber-200 bg-amber-50">
        <div className="flex items-start gap-3">
          <AlertTriangle className="text-amber-600 shrink-0 mt-0.5" size={20} />
          <div className="text-sm text-amber-900">
            <p className="font-semibold">Why reports are manual right now</p>
            <p className="mt-1">
              Counts from automated delivery tracking aren't accurate enough yet to
              show to clients unedited. Reports are gated behind this form until the
              following are improved:
            </p>
            <ul className="list-disc ml-5 mt-2 space-y-0.5">
              <li>Automated front-door detection on each delivered house</li>
              <li>Manual mark mode click-to-house snapping precision</li>
              <li>
                Parcel marker placement — dots currently sit on the centroid and can
                miss deliveries made at the actual front door
              </li>
            </ul>
          </div>
        </div>
      </Card>

      {/* Reports list */}
      {reports.length === 0 ? (
        <Card className="p-12 text-center border-dashed">
          <FileText size={42} className="mx-auto text-gray-300 mb-3" />
          <p className="text-gray-500">No manual reports yet</p>
          <p className="text-xs text-gray-400 mt-1">
            Click "New Report" to create one for a client.
          </p>
        </Card>
      ) : (
        <div className="space-y-3">
          {reports.map((r) => (
            <Card
              key={r.id}
              className="p-4 hover:shadow-md transition-shadow"
              data-testid={`report-row-${r.id}`}
            >
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{r.title}</h3>
                    <Badge className={STATUS_BADGE[r.status]?.cls || ''}>
                      {STATUS_BADGE[r.status]?.label || r.status}
                    </Badge>
                    {/* Report type tag — distinguishes the new Daily
                        Activity reports from full Completion reports in
                        a glance. Defaults to "Completion" for legacy
                        rows that pre-date the report_type field. */}
                    <Badge
                      className={
                        (r.report_type || 'completion') === 'daily'
                          ? 'bg-blue-100 text-blue-700 border-blue-300'
                          : 'bg-purple-100 text-purple-700 border-purple-300'
                      }
                      data-testid={`report-type-badge-${r.id}`}
                    >
                      {(r.report_type || 'completion') === 'daily' ? 'Daily' : 'Completion'}
                    </Badge>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Job: <span className="font-medium">{r.job_title}</span> ·
                    Client: <span className="font-medium">{r.client_name || r.client_email || '—'}</span>
                  </p>
                  <div className="flex items-center gap-4 text-xs text-gray-500 mt-1.5">
                    {r.deliveries_count != null && (
                      <span className="flex items-center gap-1">
                        <Package size={12} /> {r.deliveries_count} deliveries
                      </span>
                    )}
                    {/* Date — Daily reports show activity_date, completion
                        reports show the start → end range. */}
                    {(r.report_type === 'daily' ? r.activity_date : r.delivery_date_start) && (
                      <span className="flex items-center gap-1">
                        <Calendar size={12} />
                        {r.report_type === 'daily'
                          ? r.activity_date
                          : (
                            <>
                              {r.delivery_date_start}
                              {r.delivery_date_end ? ` → ${r.delivery_date_end}` : ''}
                            </>
                          )}
                      </span>
                    )}
                    {(r.photos || []).length > 0 && (
                      <span className="flex items-center gap-1">
                        <ImageIcon size={12} /> {(r.photos || []).length} photo
                        {(r.photos || []).length === 1 ? '' : 's'}
                      </span>
                    )}
                    <span>By {r.created_by_name || 'staff'}</span>
                  </div>
                  {/* Last-sent hint — surfaces the most recent
                      resend-to-client timestamp so the admin doesn't
                      double-email a customer. Hidden when the report has
                      never been emailed to the client. */}
                  {r.last_sent_to_client_at && (
                    <p
                      className="text-[11px] text-blue-700 mt-1 flex items-center gap-1"
                      title={`Sent ${new Date(r.last_sent_to_client_at).toLocaleString()} to ${r.last_sent_to_client_email || r.client_email || 'client'}`}
                      data-testid={`last-sent-${r.id}`}
                    >
                      <Mail size={11} />
                      Last sent {_relTime(r.last_sent_to_client_at)}
                      {(r.last_sent_to_client_email || r.client_email)
                        ? <> to <span className="font-medium">{r.last_sent_to_client_email || r.client_email}</span></>
                        : null}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                  {/* View Link — opens the branded client portal in a new
                      tab and copies the URL to the clipboard so the admin
                      can also paste it into a customer chat. Only useful
                      once the report is published. */}
                  {r.status === 'published' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openClientPortal(r)}
                      disabled={linkLoadingId === r.id}
                      className="text-purple-700 border-purple-300 hover:bg-purple-50"
                      data-testid={`view-link-report-${r.id}`}
                      title={`Opens ${r.client_email || 'the client portal'} — client must sign in to view`}
                    >
                      {linkLoadingId === r.id ? (
                        <Loader2 size={14} className="animate-spin mr-1" />
                      ) : (
                        <ExternalLink size={14} className="mr-1" />
                      )}
                      View Link
                    </Button>
                  )}
                  {/* Re-send — fires the same email the publish flow sends
                      to the client's saved email. Backend rejects drafts so
                      we hide the button until the report is published. */}
                  {r.status === 'published' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => resendToClient(r)}
                      disabled={resendingId === r.id || !r.client_email}
                      className="text-blue-700 border-blue-300 hover:bg-blue-50 disabled:opacity-50"
                      data-testid={`resend-report-${r.id}`}
                      title={
                        r.client_email
                          ? `Re-send report email to ${r.client_email}`
                          : 'No client email on this report — edit it to add one before re-sending.'
                      }
                    >
                      {resendingId === r.id ? (
                        <Loader2 size={14} className="animate-spin mr-1" />
                      ) : (
                        <Mail size={14} className="mr-1" />
                      )}
                      Re-send
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => openEdit(r)}
                    data-testid={`edit-report-${r.id}`}
                  >
                    <Pencil size={14} className="mr-1" /> Edit
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => togglePublish(r)}
                    className={
                      r.status === 'published'
                        ? 'text-amber-600 border-amber-300 hover:bg-amber-50'
                        : 'text-emerald-700 border-emerald-300 hover:bg-emerald-50'
                    }
                    data-testid={`toggle-publish-${r.id}`}
                  >
                    {r.status === 'published' ? (
                      <>
                        <EyeOff size={14} className="mr-1" /> Unpublish
                      </>
                    ) : (
                      <>
                        <Send size={14} className="mr-1" /> Publish
                      </>
                    )}
                  </Button>
                  {canDelete && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => remove(r)}
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      data-testid={`delete-report-${r.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* ====================== FORM DIALOG ====================== */}
      <Dialog open={showForm} onOpenChange={(o) => !o && closeForm()}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle data-testid="report-dialog-title">
              {editingReport ? 'Edit Report' : 'Daily Update and Photos Report'}
            </DialogTitle>
            <DialogDescription>
              {editingReport
                ? 'Update the notes or photos for this report. The client will see your changes on their portal report page.'
                : 'Pick a job, write a short daily note, and attach photos. The update will appear at the bottom of the client portal report for that job (newest first).'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 pt-2">
            {/* Job selector — only in_progress + completed (backend enforces) */}
            <div>
              <Label htmlFor="rep-job">Job *</Label>
              <select
                id="rep-job"
                value={form.job_id}
                onChange={(e) => onJobPicked(e.target.value)}
                disabled={!!editingReport}
                className="w-full mt-1 border rounded-md px-3 py-2 text-sm bg-white disabled:bg-gray-50 disabled:text-gray-500"
                data-testid="report-job-select"
              >
                <option value="">— Select a job —</option>
                {jobs.map((j) => (
                  <option key={j.id} value={j.id}>
                    [{j.status}] {j.title} {j.client_name ? `· ${j.client_name}` : ''}
                  </option>
                ))}
              </select>
              {editingReport && (
                <p className="text-xs text-gray-400 mt-1">
                  Job is locked after creation. Delete and recreate to change.
                </p>
              )}
            </div>

            {/* Client email — explicit override so the published report
                lands in the inbox the admin expects. Leave blank to
                inherit the email on the linked job. Backend lowercases +
                trims, and the client portal looks up case-insensitively
                so a typo here won't silently hide the report. */}
            <div>
              <Label htmlFor="rep-client-email">
                Client email
                <span className="ml-2 text-[10px] uppercase tracking-wide px-1.5 py-0.5 bg-purple-100 text-purple-700 rounded align-middle">
                  Required for portal visibility
                </span>
              </Label>
              <Input
                id="rep-client-email"
                type="email"
                value={form.client_email}
                onChange={(e) => setForm({ ...form, client_email: e.target.value })}
                placeholder={preview?.client_email || 'client@example.com'}
                data-testid="report-client-email-input"
              />
              {preview?.client_email && !form.client_email && (
                <p className="text-[11px] text-gray-500 mt-1">
                  Will inherit <strong>{preview.client_email}</strong> from the job. Type a different email here to override.
                </p>
              )}
              {form.client_email && preview?.client_email
                && form.client_email.trim().toLowerCase() !== preview.client_email.toLowerCase() && (
                <p className="text-[11px] text-amber-700 mt-1">
                  ⚠ This is different from the job's <strong>{preview.client_email}</strong>.
                  The portal user must be logged in under this email to see the report.
                </p>
              )}
            </div>

            {/* Daily update notes — the single free-text field on the
                simplified report. Renders at the top of the appended
                update card on the client portal report page. */}
            <div>
              <Label htmlFor="rep-sum">Daily update notes</Label>
              <Textarea
                id="rep-sum"
                rows={4}
                value={form.summary}
                onChange={(e) => setForm({ ...form, summary: e.target.value })}
                placeholder="What happened today? This appears at the top of the update card on the client portal report."
                data-testid="report-summary"
              />
            </div>

            {/* Report Type selector / Preview maps / Title / Date Range /
                Deliveries Count / Issues / Include toggles / Worker photo
                picker — all REMOVED in the simplified "Daily Update and
                Photos Report" form (Mar 1, 2026). The form now only
                collects Job, Client email, Daily update notes, and the
                Photo attachments section directly below. Legacy fields
                still exist in the form state so editing older reports
                doesn't crash, but the UI no longer surfaces them. */}


            {/* Admin-attached photos */}
            <div className="border rounded-lg p-3">
              <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                <div>
                  <Label className="m-0">Photo attachments</Label>
                  <p className="text-[11px] text-gray-500 mt-0.5">
                    Up to 10 photos per upload — pick more once a batch finishes.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploadingPhoto || !form.job_id}
                  data-testid="upload-report-photo-btn"
                >
                  {uploadingPhoto ? (
                    <Loader2 className="animate-spin mr-1" size={14} />
                  ) : (
                    <Upload size={14} className="mr-1" />
                  )}
                  {uploadingPhoto ? 'Uploading…' : 'Upload photos'}
                </Button>
                <input
                  ref={fileRef}
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  data-testid="upload-report-photo-input"
                  onChange={(e) => {
                    if (e.target.files && e.target.files.length > 0) {
                      uploadPhotos(e.target.files);
                    }
                    e.target.value = '';
                  }}
                />
              </div>
              {!form.job_id && (
                <p className="text-xs text-gray-400">
                  Pick a job first, then you can upload photos right away.
                </p>
              )}
              {editingReport?.photos?.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 mt-2">
                  {editingReport.photos.map((p) => (
                    <div key={p.id} className="relative group">
                      <img
                        src={`${BACKEND_URL}${p.url}`}
                        alt=""
                        className="w-full h-24 object-cover rounded border"
                      />
                      <button
                        onClick={() => deletePhoto(p.id)}
                        className="absolute top-1 right-1 bg-black bg-opacity-50 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition"
                        title="Remove"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={closeForm} disabled={saving}>
              Cancel
            </Button>
            <Button
              variant="outline"
              onClick={() => save(false)}
              disabled={saving || !form.job_id}
              data-testid="save-draft-btn"
            >
              {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : null}
              Save Draft
            </Button>
            <Button
              variant="outline"
              onClick={sendPreview}
              disabled={sendingPreview || saving || !form.job_id}
              className="border-blue-500 text-blue-700 hover:bg-blue-50"
              data-testid="send-preview-btn"
              title="Email a preview of this report to info@localflyerdelivery.com"
            >
              {sendingPreview
                ? <Loader2 className="animate-spin mr-1" size={14} />
                : <Send size={14} className="mr-1" />}
              Send Preview
            </Button>
            <Button
              onClick={() => save(true)}
              disabled={saving || !form.job_id}
              className="bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="save-publish-btn"
            >
              {saving ? <Loader2 className="animate-spin mr-1" size={14} /> : <Send size={14} className="mr-1" />}
              {editingReport?.status === 'published' ? 'Save & Republish' : 'Save & Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminClientReporting;
